import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  Plus, 
  Search, 
  Edit,
  Trash2,
  Eye,
  Filter,
  Calendar,
  Tag
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { fetchPosts, deletePost, fetchCategories } from '../services/newsApi';
import CustomSelect from '../components/common/CustomSelect';

const AdminPosts = () => {
  const { isDarkMode } = useTheme();
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterPosts();
  }, [posts, searchTerm, selectedCategory, selectedStatus]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [postsData, categoriesData] = await Promise.all([
        fetchPosts(),
        fetchCategories()
      ]);
      setPosts(postsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const filterPosts = () => {
    let filtered = [...posts];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(term) ||
        (post.excerpt && post.excerpt.toLowerCase().includes(term))
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(post => post.category_id === parseInt(selectedCategory));
    } 

    if (selectedStatus !== 'all') {
      filtered = filtered.filter(post => post.status === selectedStatus);
    }

    setFilteredPosts(filtered);
  };

  const handleDelete = async (postId, postTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${postTitle}"?`)) {
      return;
    }

    try {
      await deletePost(postId);
      alert('Post deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error deleting post:', error);
      alert(error.message || 'Failed to delete post');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      published: { color: 'green', text: 'Published' },
      draft: { color: 'yellow', text: 'Draft' },
      archived: { color: 'gray', text: 'Archived' }
    };
    const badge = badges[status] || badges.draft;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
        badge.color === 'green'
          ? isDarkMode
            ? 'bg-fsgu-green-900/30 text-fsgu-green-400'
            : 'bg-fsgu-green-100 text-fsgu-green-700'
          : badge.color === 'yellow'
          ? isDarkMode
            ? 'bg-yellow-900/30 text-yellow-400'
            : 'bg-yellow-100 text-yellow-700'
          : isDarkMode
          ? 'bg-fsgu-gray-700 text-fsgu-silver-400'
          : 'bg-fsgu-silver-200 text-fsgu-silver-600'
      }`}>
        {badge.text}
      </span>
    );
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || 'Uncategorized';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not published';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
      }`}>
        <div className="text-center">
          <div className={`inline-block animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent mb-4 ${
            isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
          }`}></div>
          <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
            Loading posts...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${
      isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
    }`}>
      <div className="container-custom py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-3xl sm:text-4xl font-bold font-cursive ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              News & Posts
            </h1>
            <p className={`text-sm mt-1 ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>
              Manage news articles and blog posts
            </p>
          </div>
          <Link
            to="/admin/news/add"
            className="btn btn-primary inline-flex items-center justify-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add New Post
          </Link>
        </div>

        {/* Filters */}
        <div className={`p-6 rounded-xl border ${
          isDarkMode 
            ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
            : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
        }`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative sm:col-span-2 lg:col-span-1">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 pointer-events-none ${
                isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
              }`} />
              <input
                type="text"
                placeholder="Search posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>

            {/* Category Filter */}
            <CustomSelect
              name="selectedCategory"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              options={[
                { value: 'all', label: 'All Categories' },
                ...categories.map(cat => ({
                  value: cat.id.toString(),
                  label: cat.name
                }))
              ]}
              placeholder="All Categories"
            />

            {/* Status Filter */}
            <CustomSelect
              name="selectedStatus"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'published', label: 'Published' },
                { value: 'draft', label: 'Draft' },
                { value: 'archived', label: 'Archived' }
              ]}
              placeholder="All Status"
            />
          </div>

          {/* Results Count */}
          <div className={`mt-4 pt-4 border-t ${
            isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
          }`}>
            <p className={`text-sm ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>
              Showing <span className="font-semibold">{filteredPosts.length}</span> of <span className="font-semibold">{posts.length}</span> posts
            </p>
          </div>
        </div>

        {/* Posts Grid */}
        {filteredPosts.length === 0 ? (
          <div className={`text-center py-16 rounded-xl border ${
            isDarkMode 
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <FileText className={`w-20 h-20 mx-auto mb-4 ${
              isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-400'
            }`} />
            <h3 className={`text-xl font-semibold mb-2 ${
              isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
            }`}>
              No posts found
            </h3>
            <p className={`text-sm ${
              isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
            }`}>
              {searchTerm || selectedCategory !== 'all' || selectedStatus !== 'all'
                ? 'Try adjusting your filters' 
                : 'Get started by creating your first post'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredPosts.map(post => (
              <div
                key={post.id}
                className={`p-6 rounded-xl border transition-all hover:shadow-lg ${
                  isDarkMode 
                    ? 'bg-fsgu-gray-800 border-fsgu-gray-700 hover:border-fsgu-green-500/50' 
                    : 'bg-fsgu-white border-fsgu-silver-200 hover:border-fsgu-green-500/50 shadow-sm'
                }`}
              >
                {/* Post Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {getStatusBadge(post.status)}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                        isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
                      }`}>
                        <Tag className="w-3 h-3 mr-1" />
                        {getCategoryName(post.category_id)}
                      </span>
                    </div>
                    <h3 className={`text-xl font-bold mb-2 line-clamp-2 ${
                      isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                    }`}>
                      {post.title}
                    </h3>
                  </div>
                  <div className={`p-2 rounded-lg ml-3 ${
                    isDarkMode ? 'bg-fsgu-green-500/20' : 'bg-fsgu-green-500/10'
                  }`}>
                    <FileText className={`w-6 h-6 ${
                      isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                    }`} />
                  </div>
                </div>

                {/* Excerpt */}
                {post.excerpt && (
                  <p className={`text-sm mb-4 line-clamp-2 ${
                    isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                  }`}>
                    {post.excerpt}
                  </p>
                )}

                {/* Meta Info */}
                <div className={`flex items-center gap-4 text-xs mb-4 ${
                  isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
                }`}>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDate(post.published_at)}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Eye className="w-3.5 h-3.5" />
                    {post.views || 0} views
                  </div>
                </div>

                {/* Actions */}
                <div className={`flex gap-2 pt-4 border-t ${
                  isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
                }`}>
                  <Link
                    to={`/admin/news/edit/${post.id}`}
                    className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors inline-flex items-center justify-center ${
                      isDarkMode
                        ? 'bg-fsgu-gray-700 hover:bg-fsgu-gray-600 text-fsgu-silver-200'
                        : 'bg-fsgu-silver-100 hover:bg-fsgu-silver-200 text-fsgu-silver-700'
                    }`}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Link>
                  <button
                    onClick={() => handleDelete(post.id, post.title)}
                    className={`p-2.5 rounded-lg text-sm font-medium transition-colors inline-flex items-center justify-center ${
                      isDarkMode
                        ? 'bg-fsgu-red-900/20 hover:bg-fsgu-red-900/30 text-fsgu-red-400'
                        : 'bg-fsgu-red-50 hover:bg-fsgu-red-100 text-fsgu-red-600'
                    }`}
                    title="Delete post"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPosts;